// widget/src/hooks/useChatStream.ts
import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import type {
  CanvasPayload,
  CanvasState,
  ColorCardData,
  Message,
  MessageAttachment,
  PageContext,
  PendingBasket,
  ProductCardData,
  TrackingCardData,
  VideoCardData,
} from "../types"

interface State {
  messages: Message[]
  pending: boolean
}

// ── Session persistence (open-state continuity) ──────────────────────
// Persist the conversation to sessionStorage so the thread survives a
// same-tab page navigation (clicking a product reloads the page; the
// widget re-mounts fresh otherwise). sessionStorage is the right scope:
// it persists across navigations in one tab and clears when the tab
// closes — exactly "this browsing session".
const PERSIST_KEY = "pearl-chat-state"

interface PersistedState {
  conversationId: string | null
  messages: Message[]
  // The canvas (basket + active rich payload + history strip + open state).
  // Persisted so a same-tab navigation — e.g. the customer taps a product IN
  // the canvas and the page reloads — restores the canvas exactly as it was,
  // open, instead of dropping back to an empty/closed canvas and losing the
  // result cards. Optional so older persisted blobs (pre-canvas-persistence)
  // still load.
  canvas?: CanvasState | null
}

const EMPTY_CANVAS: CanvasState = {
  basket: null,
  activePayload: null,
  history: [],
  isOpen: false,
}

/** Coerce a parsed/persisted canvas blob back to a valid CanvasState so an
 *  old or partial sessionStorage entry can't crash the reducer. */
function normalizeCanvas(c: unknown): CanvasState {
  if (!c || typeof c !== "object") return EMPTY_CANVAS
  const o = c as Partial<CanvasState>
  return {
    basket: o.basket ?? null,
    activePayload: o.activePayload ?? null,
    history: Array.isArray(o.history) ? o.history : [],
    isOpen: Boolean(o.isOpen),
  }
}

function loadPersisted(): PersistedState | null {
  try {
    const raw = sessionStorage.getItem(PERSIST_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedState
    if (!Array.isArray(parsed.messages)) return null
    // Never restore a mid-stream pending state — mark everything settled.
    parsed.messages = parsed.messages.map((m) => ({ ...m, pending: false }))
    return parsed
  } catch {
    return null
  }
}

function savePersisted(
  conversationId: string | null,
  messages: Message[],
  canvas: CanvasState | null,
): void {
  try {
    sessionStorage.setItem(
      PERSIST_KEY,
      JSON.stringify({ conversationId, messages, canvas }),
    )
  } catch {
    /* quota / disabled — ignore */
  }
}

type Action =
  | { type: "user_send"; text: string; images?: string[] }
  | { type: "assistant_start"; messageId: string }
  | { type: "text_delta"; messageId: string; text: string }
  | { type: "text_replace"; messageId: string; text: string }
  | { type: "tool_call_start"; messageId: string; toolId: string; name: string }
  | { type: "tool_call_result"; messageId: string; toolId: string }
  | { type: "attachments_add"; messageId: string; attachments: MessageAttachment[] }
  | { type: "chips"; messageId: string; chips: string[] }
  | { type: "done"; messageId: string }
  | { type: "error"; messageId: string; error: string }
  | { type: "reset" }
  | { type: "hydrate"; messages: Message[] }
  // Append agent rows polled from the server. Each is rendered as an
  // assistant-style bubble with a `senderName` so the meta line shows the
  // human teammate's name instead of "Pearl". Dedup by id at the reducer
  // boundary defends against an overlapping poll firing before the previous
  // append has settled into state.
  | { type: "append_agent"; messages: Message[] }

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case "user_send":
      return {
        ...state,
        pending: true,
        messages: [
          ...state.messages,
          {
            id: crypto.randomUUID(),
            role: "user",
            text: action.text,
            images: action.images?.length ? action.images : undefined,
          },
        ],
      }
    case "assistant_start":
      return {
        ...state,
        messages: [
          ...state.messages,
          { id: action.messageId, role: "assistant", text: "", pending: true },
        ],
      }
    case "text_delta":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId ? { ...m, text: m.text + action.text } : m,
        ),
      }
    case "text_replace":
      // text_clean event — replace streamed text with the post-processed
      // version (strips <<CHIPS>>/<<INTENT>> tags). Keeps token-by-token
      // streaming visible until the cleanup arrives at the end.
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId ? { ...m, text: action.text } : m,
        ),
      }
    case "tool_call_start":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id !== action.messageId
            ? m
            : {
                ...m,
                tools: [
                  ...(m.tools ?? []),
                  { id: action.toolId, name: action.name, status: "pending" },
                ],
              },
        ),
      }
    case "tool_call_result":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id !== action.messageId
            ? m
            : {
                ...m,
                tools: (m.tools ?? []).map((t) =>
                  t.id === action.toolId ? { ...t, status: "ok" } : t,
                ),
              },
        ),
      }
    case "attachments_add":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id !== action.messageId
            ? m
            : {
                ...m,
                attachments: [...(m.attachments ?? []), ...action.attachments],
              },
        ),
      }
    case "chips":
      return {
        ...state,
        messages: state.messages.map((m) =>
          m.id === action.messageId ? { ...m, chips: action.chips } : m,
        ),
      }
    case "done":
      return {
        ...state,
        pending: false,
        messages: state.messages.map((m) =>
          m.id === action.messageId ? { ...m, pending: false } : m,
        ),
      }
    case "error":
      return {
        ...state,
        pending: false,
        messages: state.messages.map((m) =>
          m.id === action.messageId
            ? {
                ...m,
                pending: false,
                text: m.text || `(error: ${action.error})`,
              }
            : m,
        ),
      }
    case "reset":
      return { messages: [], pending: false }
    case "hydrate":
      return { messages: action.messages, pending: false }
    case "append_agent": {
      if (action.messages.length === 0) return state
      const existing = new Set(state.messages.map((m) => m.id))
      const fresh = action.messages.filter((m) => !existing.has(m.id))
      if (fresh.length === 0) return state
      return { ...state, messages: [...state.messages, ...fresh] }
    }
  }
}

interface UseChatStreamOptions {
  apiBaseUrl: string
  sessionToken: string
  pageContext: PageContext
  /** When true, rich action attachments route to the canvas instead of
   *  inline message bubbles. */
  canvasEnabled?: boolean
}

/** Insert `next` into canvas history. Three rules:
 *
 *  1. Build payloads (build_step + build_summary) for the same productHandle
 *     collapse into a single entry — without this, a 4-axis build produces
 *     4 chips all reading "Anthracite Grey build".
 *
 *  2. Single-product (`product`) payloads are TRANSIENT — they fire during
 *     add-to-basket flows where Pearl flashes each accessory tile before
 *     adding it. Adding 5 accessories shouldn't leave "3M Respirator /
 *     Paint Stirrer / Spray Gun Cleaning Kit / Canvas Wheel Covers /
 *     Aftercare" cluttering the back-nav history.
 *
 *  3. Multi-product (`products`) results ARE destinations — they're an
 *     explicit catalog query the customer just made. Skipping them used
 *     to break back-navigation between successive searches (caught
 *     2026-06-03: customer ran 'peelable car kits' then 'Luna One', the
 *     back-arrow never appeared because the first products payload was
 *     skipped). Now products DO add to history so back-arrow works.
 *
 *  Color/colors/tracking/video/knowledge/size_guide DO stay in history —
 *  they're meaningful destinations the customer might revisit.
 */
function mergeHistory(
  next: CanvasPayload,
  prev: CanvasPayload[],
): CanvasPayload[] {
  if (next.kind === "product") {
    return prev.slice(0, 8)
  }
  const handleOf = (p: CanvasPayload): string | null => {
    if (p.kind === "build_step" || p.kind === "build_summary") {
      return p.data.productHandle
    }
    return null
  }
  const nextHandle = handleOf(next)
  if (nextHandle) {
    const filtered = prev.filter(
      (p) =>
        !((p.kind === "build_step" || p.kind === "build_summary") &&
          handleOf(p) === nextHandle),
    )
    return [next, ...filtered].slice(0, 8)
  }
  return [next, ...prev].slice(0, 8)
}

export function useChatStream(opts: UseChatStreamOptions) {
  // Lazy-init from sessionStorage so a same-tab navigation restores the
  // thread instead of starting fresh.
  const persisted = typeof window !== "undefined" ? loadPersisted() : null
  const [state, dispatch] = useReducer(reducer, {
    messages: persisted?.messages ?? [],
    pending: false,
  })
  // True if we restored a prior conversation — ChatWidget uses this (plus
  // the "was open" flag) to decide whether to reopen on mount.
  const hadPersistedHistory = (persisted?.messages.length ?? 0) > 0

  // ── Canvas state (mini-cart Phase 1) ────────────────────────────────
  // Holds the pending basket, the currently-active rich payload, and a
  // small history strip. When `canvasEnabled` is on, action SSE events
  // route here instead of producing inline bubble attachments.
  // Lazy-init from sessionStorage too, so a same-tab navigation restores the
  // canvas (open, with its result cards + history) instead of resetting to
  // empty/closed — the "click a product → page loads → canvas is gone" bug.
  const [canvas, setCanvas] = useState<CanvasState>(
    persisted?.canvas ? normalizeCanvas(persisted.canvas) : EMPTY_CANVAS,
  )

  // The backend creates a chat_conversations row on the first message and
  // echoes its id in the `done` SSE event. Persisting it across calls is
  // the difference between Pearl having memory of the previous turn and
  // restarting with no history every time the user types.
  const conversationIdRef = useRef<string | null>(persisted?.conversationId ?? null)

  // Cursor for the agent-message polling loop (defined further down). Hoisted
  // here because both `newConversation` and `loadConversation` reset it when
  // they swap conversations, and they appear earlier in the function body than
  // the effect that uses it.
  const agentPollSinceRef = useRef<string>(new Date().toISOString())

  // Persist whenever messages OR the canvas settle (not mid-stream), so a
  // navigation mid-conversation keeps the thread + the canvas (open, with its
  // result cards) + threads into the same backend row.
  useEffect(() => {
    if (state.pending) return
    const hasCanvasContent =
      !!canvas.activePayload || canvas.history.length > 0 || !!canvas.basket
    if (state.messages.length === 0 && !hasCanvasContent) return
    savePersisted(conversationIdRef.current, state.messages, canvas)
  }, [state.messages, state.pending, canvas])

  // Typewriter buffer: tokens land here, a single setInterval drains a
  // proportional chunk per tick at ~60fps. This decouples the visual
  // reveal cadence from network arrival — Anthropic's stream often
  // arrives in bursts (5-30 chars at a time, then a pause), which makes
  // the bubble feel jumpy if rendered raw. The buffer smooths it to a
  // consistent typewriter feel.
  const bufferRef = useRef<{ messageId: string; pending: string }>({
    messageId: "",
    pending: "",
  })
  const drainTimerRef = useRef<number | null>(null)

  function ensureDrain() {
    if (drainTimerRef.current !== null) return
    drainTimerRef.current = window.setInterval(() => {
      const buf = bufferRef.current
      if (!buf.pending || !buf.messageId) {
        if (drainTimerRef.current !== null) {
          window.clearInterval(drainTimerRef.current)
          drainTimerRef.current = null
        }
        return
      }
      // Catch-up sizing: bigger chunks when the buffer is deep so we
      // don't fall behind on long replies, smaller when shallow for
      // smoothness on short ones.
      const chunkSize = Math.max(2, Math.ceil(buf.pending.length / 12))
      const chunk = buf.pending.slice(0, chunkSize)
      buf.pending = buf.pending.slice(chunkSize)
      dispatch({ type: "text_delta", messageId: buf.messageId, text: chunk })
    }, 16)
  }

  function flushBuffer() {
    const buf = bufferRef.current
    if (buf.pending && buf.messageId) {
      dispatch({ type: "text_delta", messageId: buf.messageId, text: buf.pending })
      buf.pending = ""
    }
    if (drainTimerRef.current !== null) {
      window.clearInterval(drainTimerRef.current)
      drainTimerRef.current = null
    }
  }

  // ── Send queue ───────────────────────────────────────────────────────
  // Serialize rapid sends so a chip-tap or programmatic send fired
  // mid-stream doesn't race the in-flight turn (the web composer is already
  // disabled during `pending`, but chips / window.Pearl.send() bypass that).
  // While a turn streams, new sends queue and drain one-by-one when it ends.
  // The whole burst lands in conversation history, so Pearl's next turn sees
  // all of it (see the "Multiple messages in one burst" system-prompt rule,
  // which tells her to lead with the highest-intent message).
  const inFlightRef = useRef(false)
  const sendQueueRef = useRef<
    { text: string; attachments: OutgoingAttachment[] }[]
  >([])
  const runTurnRef = useRef<
    ((text: string, attachments?: OutgoingAttachment[]) => void) | null
  >(null)

  const runTurn = useCallback(
    async (text: string, attachments: OutgoingAttachment[] = []) => {
      const assistantId = crypto.randomUUID()
      dispatch({
        type: "user_send",
        text,
        images: attachments.map((a) => a.url),
      })
      dispatch({ type: "assistant_start", messageId: assistantId })

      try {
        const res = await fetch(`${opts.apiBaseUrl}/api/chat/message`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            message: text,
            sessionToken: opts.sessionToken,
            conversationId: conversationIdRef.current ?? undefined,
            attachments: attachments.length ? attachments : undefined,
            ctx: opts.pageContext,
          }),
        })
        if (!res.ok || !res.body) {
          dispatch({
            type: "error",
            messageId: assistantId,
            error: `HTTP ${res.status}`,
          })
          return
        }

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buf = ""
        // We pair tool_start ↔ tool_result by FIFO order — the backend
        // emits them sequentially per tool call (see route.ts:365–378).
        const pendingToolStack: { id: string; name: string }[] = []

        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buf += decoder.decode(value, { stream: true })
          // SSE frames are separated by a blank line. Use a regex tolerant
          // of \r\n endings.
          const parts = buf.split(/\r?\n\r?\n/)
          buf = parts.pop() ?? ""

          for (const frame of parts) {
            const eventLine = frame.match(/^event:\s*(.+)$/m)?.[1]?.trim()
            const dataLine = frame.match(/^data:\s*(.+)$/m)?.[1]
            if (!eventLine || !dataLine) continue

            let data: Record<string, unknown> = {}
            try {
              data = JSON.parse(dataLine) as Record<string, unknown>
            } catch {
              continue
            }

            switch (eventLine) {
              case "token": {
                const t = String(data.text ?? data.token ?? "")
                if (t) {
                  bufferRef.current.messageId = assistantId
                  bufferRef.current.pending += t
                  ensureDrain()
                }
                break
              }
              case "text_clean": {
                // Flush buffered tokens before swapping to the cleaned
                // version, otherwise the typewriter races with the
                // replace and the user sees a flicker.
                flushBuffer()
                const t = String(data.text ?? "")
                if (t)
                  dispatch({
                    type: "text_replace",
                    messageId: assistantId,
                    text: t,
                  })
                break
              }
              case "tool_start": {
                const name = String(data.tool ?? data.name ?? "?")
                const id = `${name}-${crypto.randomUUID().slice(0, 8)}`
                pendingToolStack.push({ id, name })
                dispatch({
                  type: "tool_call_start",
                  messageId: assistantId,
                  toolId: id,
                  name,
                })
                break
              }
              case "tool_result": {
                const name = String(data.tool ?? data.name ?? "?")
                // Find oldest pending entry that matches this name.
                const idx = pendingToolStack.findIndex((t) => t.name === name)
                const matched =
                  idx >= 0 ? pendingToolStack.splice(idx, 1)[0] : null
                dispatch({
                  type: "tool_call_result",
                  messageId: assistantId,
                  toolId: matched?.id ?? `${name}-orphan`,
                })
                break
              }
              case "action": {
                // ── Basket events (mini-cart) — always route to canvas ──
                if (data.type === "basket_update") {
                  const basket = data.basket as import("../types").PendingBasket
                  const basketPayload: CanvasPayload = {
                    id: "basket",
                    kind: "basket",
                    pinned: true,
                  }
                  setCanvas((c) => ({
                    basket,
                    activePayload: basketPayload,
                    history: [
                      basketPayload,
                      ...c.history.filter((p) => p.kind !== "basket"),
                    ].slice(0, 8),
                    isOpen: true,
                  }))
                  handleSideEffectAction(data)
                  break
                }
                if (data.type === "basket_committed") {
                  setCanvas((c) => ({
                    basket: { items: [], subtotal: 0, updated_at: null },
                    activePayload: null,
                    history: c.history,
                    isOpen: c.isOpen,
                  }))
                  handleSideEffectAction(data)
                  break
                }

                // ── Canvas routing for OTHER rich-card actions ──
                if (opts.canvasEnabled) {
                  let payload: CanvasPayload | null = null
                  const now = Date.now()

                  if (data.type === "product_card" && data.product) {
                    const p = data.product as Record<string, unknown>
                    const variants =
                      (p.variants as Array<Record<string, unknown>>) ?? []
                    const variantPrices = variants
                      .map((v) => parseFloat(String(v.price ?? "")))
                      .filter((n) => Number.isFinite(n))
                    const minPrice = variantPrices.length
                      ? Math.min(...variantPrices)
                      : null
                    payload = {
                      id: `pc-${now}`,
                      kind: "product",
                      data: {
                        handle: String(p.handle ?? ""),
                        name: String(p.title ?? ""),
                        imageUrl: (p.image as string | null | undefined) ?? undefined,
                        summary: stripHtml(String(p.description ?? "")) || undefined,
                        productUrl: String(p.url ?? ""),
                        price:
                          (p.price as string | number | undefined)?.toString() ??
                          (minPrice != null ? String(minPrice) : undefined),
                        compareAtPrice:
                          (p.compareAtPrice as string | number | undefined)?.toString() ??
                          undefined,
                      },
                    }
                  } else if (data.type === "products") {
                    const products =
                      (data.products as Array<Record<string, unknown>>) ?? []
                    payload = {
                      id: `ps-${now}`,
                      kind: "products",
                      // Canvas density bumped from 4 → 8 on 2026-06-03: with
                      // the denser 150px grid + dropped descriptions, 8 fits
                      // comfortably as a 2-col × 4-row (or 3-col × 3-row at
                      // wider widths) without scrolling on the standard
                      // 600px-tall canvas. Lets Pearl surface meaningful
                      // breadth instead of feeling like a teaser.
                      data: products.slice(0, 8).flatMap((p) => {
                        const handle = String(p.handle ?? "")
                        const url = String(p.url ?? "")
                        if (!handle || !url) return []
                        return [
                          {
                            handle,
                            name: String(p.name ?? ""),
                            imageUrl: (p.image as string | null | undefined) ?? undefined,
                            summary: stripHtml(String(p.description ?? "")) || undefined,
                            productUrl: url,
                            price:
                              (p.price as string | number | undefined)?.toString() ??
                              undefined,
                            compareAtPrice:
                              (p.compareAtPrice as string | number | undefined)?.toString() ??
                              (p.compare_at_price as string | number | undefined)?.toString() ??
                              undefined,
                          },
                        ]
                      }),
                    }
                  } else if (data.type === "colors") {
                    const colors =
                      (data.colors as Array<Record<string, unknown>>) ?? []
                    payload = {
                      id: `cl-${now}`,
                      kind: "colors",
                      data: colors.slice(0, 5).map((c) => ({
                        name: String(c.color_name ?? ""),
                        code: String(c.color_code ?? ""),
                        brand: String(c.brand_name ?? ""),
                        finish: c.finish as string | undefined,
                        swatchUrl: String(c.image_url ?? ""),
                        rgbHex: (c.rgb_hex as string | undefined) ?? undefined,
                        productUrl: c.product_url as string | undefined,
                        renders:
                          (c.renders as
                            | { matte?: string; gloss?: string }
                            | undefined) ?? undefined,
                      })),
                    }
                  } else if (data.type === "build_car_kit_step") {
                    payload = {
                      id: `bs-${now}`,
                      kind: "build_step",
                      data: {
                        productHandle: data.productHandle as string,
                        partialSummary: data.partialSummary as never,
                        nextStep: data.nextStep as never,
                      },
                    }
                  } else if (data.type === "build_car_kit_summary") {
                    payload = {
                      id: `bm-${now}`,
                      kind: "build_summary",
                      data: {
                        productHandle: data.productHandle as string,
                        variantId: data.variantId as number,
                        variantTitle: data.variantTitle as string,
                        price: data.price as number,
                        compareAtPrice: data.compareAtPrice as number | undefined,
                        kitContents: data.kitContents as never,
                        upsellProducts: data.upsellProducts as never,
                        activeTags: data.activeTags as string[],
                        addToCartUrl: data.addToCartUrl as string,
                        colorRenders: data.colorRenders as never,
                      },
                    }
                  } else if (data.type === "order_status") {
                    const tl = (data.timeline as Array<Record<string, unknown>> | undefined) ?? []
                    const le = data.lastEvent as Record<string, unknown> | null | undefined
                    payload = {
                      id: `tr-${now}`,
                      kind: "tracking",
                      data: {
                        orderNumber: String(data.orderNumber ?? ""),
                        status: String(data.status ?? ""),
                        fulfillmentStatus: String(data.fulfillmentStatus ?? "UNFULFILLED"),
                        carrier: (data.carrier as string | null | undefined) ?? undefined,
                        trackingNumber: (data.trackingNumber as string | null | undefined) ?? undefined,
                        trackingUrl: (data.trackingUrl as string | null | undefined) ?? undefined,
                        shipDate: (data.shipDate as string | null | undefined) ?? undefined,
                        eta: (data.eta as string | null | undefined) ?? undefined,
                        deliveredAt: (data.deliveredAt as string | null | undefined) ?? undefined,
                        shipToCity: (data.shipToCity as string | null | undefined) ?? undefined,
                        lastEvent: le
                          ? {
                              occurredAt: String(le.occurredAt ?? ""),
                              description: String(le.description ?? ""),
                              location: String(le.location ?? ""),
                            }
                          : undefined,
                        timeline: tl.map((e) => ({
                          occurredAt: String(e.occurredAt ?? ""),
                          description: String(e.description ?? ""),
                          location: String(e.location ?? ""),
                        })),
                        items: (data.items as Array<{ name: string; quantity: number }> | undefined) ?? [],
                        total: (data.total as string | null | undefined) ?? undefined,
                      },
                    }
                  } else if (data.type === "knowledge_results") {
                    const articles =
                      (data.articles as Array<Record<string, unknown>>) ?? []
                    const videos =
                      (data.videos as Array<Record<string, unknown>>) ?? []
                    if (articles.length > 0 || videos.length > 0) {
                      payload = {
                        id: `kb-${now}`,
                        kind: "knowledge",
                        data: {
                          query: String(data.query ?? ""),
                          articles: articles.map((a) => ({
                            title: String(a.title ?? "(untitled)"),
                            url: a.url as string | undefined,
                            preview: String(a.preview ?? ""),
                            category: a.category as string | undefined,
                            content:
                              typeof a.content === "string" && a.content.trim()
                                ? a.content
                                : undefined,
                          })),
                          videos: videos.map((v) => ({
                            videoId: String(v.videoId ?? ""),
                            title: String(v.title ?? ""),
                            thumbnail: String(v.thumbnail ?? ""),
                            url: String(v.url ?? ""),
                          })),
                        },
                      }
                    }
                  } else if (data.type === "size_guide") {
                    const products = (data.products as string[] | undefined) ?? []
                    payload = {
                      id: `sg-${now}`,
                      kind: "size_guide",
                      data: {
                        projectLabel: String(
                          data.project_label ?? data.projectLabel ?? data.project_type ?? "Project",
                        ),
                        recommendedSize: String(data.recommendedSize ?? ""),
                        explanation: String(data.explanation ?? ""),
                        products: products.map((p) => ({ label: String(p) })),
                      },
                    }
                  } else if (
                    data.type === "youtube_video" ||
                    data.type === "youtube_videos"
                  ) {
                    const videos =
                      data.type === "youtube_video"
                        ? [data]
                        : ((data.videos as Array<Record<string, unknown>>) ?? [])
                    if (videos.length > 0) {
                      const v = videos[0] as Record<string, unknown>
                      payload = {
                        id: `vd-${now}`,
                        kind: "video",
                        data: {
                          videoId: String(v.videoId ?? v.video_id ?? ""),
                          title: String(v.title ?? ""),
                          thumbnail: String(v.thumbnail ?? ""),
                          url: String(v.url ?? ""),
                        },
                      }
                    }
                  }
                  // Note: discount, navigate, cart_added, handoff, vehicle,
                  // size_guide, collection, image, videos aren't canvas-worthy
                  // in Phase 1 — fall through to inline rendering.

                  if (payload) {
                    const finalPayload = payload
                    setCanvas((c) => ({
                      ...c,
                      activePayload: finalPayload,
                      // Collapse a sequence of build_step/build_summary payloads
                      // for the same productHandle into a single history entry —
                      // otherwise every axis pick (size → sprayer → finish →
                      // summary) creates a duplicate "Anthracite Grey build" chip
                      // and the strip becomes unreadable.
                      history: mergeHistory(finalPayload, c.history),
                      // Conversational-first for KNOWLEDGE (guide articles):
                      // set the content so the canvas-toggle button appears, but
                      // do NOT throw the canvas open on a light FAQ — that was
                      // the jarring "auto-opening with tangential cards" behavior
                      // (Justin 2026-06-15/16; deterministic backstop to prompt
                      // rule 4a). Product / colors / kit-builder / size guide /
                      // tracking ARE the upsell surface → still auto-open.
                      isOpen: finalPayload.kind === "knowledge" ? c.isOpen : true,
                    }))
                    handleSideEffectAction(data)
                    break
                  }
                }

                // ── Default: inline-attachment rendering ──
                // The backend emits structured side-effect / rich-card
                // payloads via `action`. Convert recognised types into
                // attachments rendered under the assistant bubble.
                const attachments = mapActionToAttachments(data)
                if (attachments.length > 0) {
                  dispatch({
                    type: "attachments_add",
                    messageId: assistantId,
                    attachments,
                  })
                }
                handleSideEffectAction(data)
                break
              }
              case "chips": {
                const chips = Array.isArray(data.chips)
                  ? (data.chips as string[])
                  : []
                if (chips.length > 0)
                  dispatch({ type: "chips", messageId: assistantId, chips })
                break
              }
              case "intent":
                // Currently informational; ignore in v1.
                break
              case "done": {
                // Capture the conversation id so subsequent sends thread
                // into the same chat_conversations row (and Pearl sees
                // history). The backend creates the row on the first
                // message if no id was supplied.
                const cid = data.conversationId
                if (typeof cid === "string" && cid.length > 0) {
                  conversationIdRef.current = cid
                }
                flushBuffer()
                dispatch({ type: "done", messageId: assistantId })
                break
              }
              case "error": {
                flushBuffer()
                // The server emits `detail` only in non-production builds
                // (see /api/chat/message route). Prefer it when present so
                // a local-dev tester sees the actual cause (e.g. Shopify
                // 401, parse failure, Anthropic rate limit) instead of the
                // generic "Something went wrong" boilerplate.
                const msg = String(data.message ?? data.error ?? "unknown")
                const detail =
                  typeof data.detail === "string" && data.detail
                    ? ` (${data.detail})`
                    : ""
                dispatch({
                  type: "error",
                  messageId: assistantId,
                  error: `${msg}${detail}`,
                })
                break
              }
              default:
                // Unknown event — skip silently.
                break
            }
          }
        }

        // Stream ended without an explicit `done` — finalize anyway.
        dispatch({ type: "done", messageId: assistantId })
      } catch (err) {
        dispatch({
          type: "error",
          messageId: assistantId,
          error: err instanceof Error ? err.message : "unknown",
        })
      } finally {
        // Drain the queue. If another send was fired while this turn
        // streamed, run it next on a fresh tick (lets the reducer settle
        // the `done`/`error` state before re-entry). inFlightRef stays true
        // through the whole drain so newly-arriving sends keep queueing
        // rather than racing — it only clears when the queue is empty.
        const next = sendQueueRef.current.shift()
        if (next) {
          setTimeout(() => {
            void runTurnRef.current?.(next.text, next.attachments)
          }, 0)
        } else {
          inFlightRef.current = false
        }
      }
    },
    [opts.apiBaseUrl, opts.sessionToken, opts.pageContext],
  )

  // Keep a stable ref to the latest runTurn for the queue-drain re-entry
  // above (avoids a self-referential useCallback dependency).
  useEffect(() => {
    runTurnRef.current = runTurn
  }, [runTurn])

  // Public send. Guards against overlapping turns: if one is already
  // streaming, queue this send and return — it drains when the current turn
  // finishes (see runTurn's `finally`). Otherwise mark in-flight and run.
  // The guard check + set are synchronous, so two taps in the same tick
  // can't both pass.
  const send = useCallback(
    (text: string, attachments: OutgoingAttachment[] = []) => {
      if (inFlightRef.current) {
        sendQueueRef.current.push({ text, attachments })
        return
      }
      inFlightRef.current = true
      void runTurn(text, attachments)
    },
    [runTurn],
  )

  // Clear the persisted thread (called on explicit close so the widget
  // doesn't reopen to a stale conversation on the next navigation).
  const clearPersisted = useCallback(() => {
    try {
      sessionStorage.removeItem(PERSIST_KEY)
    } catch {
      /* ignore */
    }
  }, [])

  // Start a fresh conversation: drop the current thread + backend id so the
  // next send creates a new chat_conversations row. (Header menu → "New
  // conversation".)
  const newConversation = useCallback(() => {
    flushBuffer()
    conversationIdRef.current = null
    dispatch({ type: "reset" })
    // Canvas state needs the same reset — without this, the prior chat's
    // basket + activePayload + history strip bleed into the new
    // conversation, e.g. "Video / 5 colors" chips still rendering at the
    // bottom of a freshly-started chat where Pearl hasn't fired anything.
    setCanvas({
      basket: null,
      activePayload: null,
      history: [],
      isOpen: false,
    })
    try {
      sessionStorage.removeItem(PERSIST_KEY)
    } catch {
      /* ignore */
    }
    // Fresh conversation → polling cursor restarts from "now" so the first
    // poll after the conversation row exists doesn't pull anything stale.
    agentPollSinceRef.current = new Date().toISOString()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // List this session's past conversations (header menu).
  const listConversations = useCallback(async (): Promise<ConversationSummary[]> => {
    try {
      const r = await fetch(
        `${opts.apiBaseUrl}/api/chat/conversations?sessionToken=${encodeURIComponent(opts.sessionToken)}`,
      )
      if (!r.ok) return []
      const data = (await r.json()) as { conversations?: ConversationSummary[] }
      return data.conversations ?? []
    } catch {
      return []
    }
  }, [opts.apiBaseUrl, opts.sessionToken])

  // Reopen a past conversation: fetch its messages and hydrate the thread.
  const loadConversation = useCallback(
    async (id: string) => {
      try {
        const r = await fetch(
          `${opts.apiBaseUrl}/api/chat/conversation?sessionToken=${encodeURIComponent(opts.sessionToken)}&conversationId=${encodeURIComponent(id)}`,
        )
        if (!r.ok) return
        const data = (await r.json()) as { messages?: Message[] }
        const msgs = data.messages ?? []
        conversationIdRef.current = id
        dispatch({ type: "hydrate", messages: msgs })
        // Switching conversations: the prior conversation's canvas is stale
        // (we don't reconstruct a loaded conversation's canvas from the
        // server), so reset it rather than bleed it across threads.
        setCanvas(EMPTY_CANVAS)
        savePersisted(id, msgs, EMPTY_CANVAS)
        // Loading a past conversation resets the agent-poll cursor — anything
        // newer than `now` is freshly-incoming, anything in `msgs` is already
        // rendered.
        agentPollSinceRef.current = new Date().toISOString()
      } catch {
        /* ignore */
      }
    },
    [opts.apiBaseUrl, opts.sessionToken],
  )

  // ── Agent-message polling ─────────────────────────────────────────────
  //
  // The widget receives Pearl AI replies inline via the SSE response of the
  // customer's own POST /api/chat/message — but agent (human teammate)
  // replies are inserted into chat_messages from the staff dashboard with no
  // stream pointed at the widget. Without polling, those rows sit in the DB
  // and never appear on the customer's screen.
  //
  // Implementation: while a conversation exists + the tab is visible + the
  // SSE stream isn't currently in flight, GET /api/chat/conversation/agent-poll
  // every POLL_MS milliseconds and append any new agent rows. The endpoint
  // is scoped to role='agent' so id-dedup is safe (see the route's docblock
  // for the assistant-id reconciliation problem this sidesteps).
  //
  // `since` is advanced after each successful poll to the latest row's
  // created_at, so subsequent polls cost an indexed range scan and return
  // only truly-new rows.
  const POLL_MS = 5000
  // `agentPollSinceRef` is hoisted to the top of the hook (so the conversation
  // reset helpers can rewind it). The `stateRef` mirror lets the polling
  // closure read the latest `pending` flag without re-subscribing every render.
  const stateRef = useRef(state)
  stateRef.current = state

  useEffect(() => {
    if (typeof window === "undefined") return

    let cancelled = false
    let timer: number | null = null

    async function tick() {
      if (cancelled) return
      const conversationId = conversationIdRef.current
      const hidden =
        typeof document !== "undefined" && document.visibilityState === "hidden"
      // Skip when there's nothing to poll, the tab is hidden, or we're
      // mid-stream (don't disturb the typewriter buffer / pending bubble).
      if (!conversationId || hidden || stateRef.current.pending) {
        scheduleNext()
        return
      }
      try {
        const url =
          `${opts.apiBaseUrl}/api/chat/conversation/agent-poll` +
          `?sessionToken=${encodeURIComponent(opts.sessionToken)}` +
          `&conversationId=${encodeURIComponent(conversationId)}` +
          `&since=${encodeURIComponent(agentPollSinceRef.current)}`
        const r = await fetch(url)
        if (r.ok) {
          const data = (await r.json()) as {
            messages?: Array<{
              id: string
              text: string
              displayData?: Record<string, unknown>
              senderName?: string
              createdAt: string
            }>
          }
          const rows = data.messages ?? []
          if (rows.length > 0) {
            const incoming: Message[] = rows.map((m) => {
              // Translate agent display_data into either attachments (cards)
              // or augmented text (markdown image). See mapAgentDisplayData
              // below for the per-type shape adapters — the dashboard's
              // send-card route writes product_card payloads identical to
              // Pearl's, but reply-input writes singular `youtube_video` and
              // `image` shapes that need a tiny adapter.
              const adapted = mapAgentDisplayData(m.displayData)
              return {
                id: m.id,
                // Render agent rows as assistant-style bubbles; the meta line
                // swaps "Pearl" for senderName so the customer can see who's
                // really replying.
                role: "assistant",
                text: adapted.text ?? m.text,
                senderName: m.senderName,
                attachments: adapted.attachments,
              }
            })
            dispatch({ type: "append_agent", messages: incoming })
            // Advance the cursor to the newest row's createdAt so the next
            // poll's range scan starts after this batch. Server orders
            // ascending → last row is newest.
            const newest = rows[rows.length - 1].createdAt
            agentPollSinceRef.current = newest
          }
        }
      } catch {
        // Network / aborted — silent retry on next tick.
      } finally {
        scheduleNext()
      }
    }

    function scheduleNext() {
      if (cancelled) return
      timer = window.setTimeout(tick, POLL_MS)
    }

    // Kick off the first poll on a short delay so a fresh page load doesn't
    // race the session token mint + initial render.
    timer = window.setTimeout(tick, 1500)

    return () => {
      cancelled = true
      if (timer !== null) {
        window.clearTimeout(timer)
        timer = null
      }
    }
  }, [opts.apiBaseUrl, opts.sessionToken])

  return {
    messages: state.messages,
    pending: state.pending,
    send,
    hadPersistedHistory,
    clearPersisted,
    newConversation,
    listConversations,
    loadConversation,
    currentConversationId: conversationIdRef,
    // ── Canvas (mini-cart Phase 1) ────────────────────────────────────
    canvas,
    setActivePayload: (id: string) =>
      setCanvas((c) => ({
        ...c,
        activePayload: c.history.find((p) => p.id === id) ?? c.activePayload,
      })),
    closeCanvas: () => setCanvas((c) => ({ ...c, isOpen: false })),
    openCanvas: () => setCanvas((c) => ({ ...c, isOpen: true })),
    /** Silent basket update — POSTs to /api/chat/basket, applies the
     *  returned basket without going through the chat stream. Used by
     *  the canvas BasketView's qty steppers + remove buttons so a
     *  customer fiddling with quantities doesn't echo every click into
     *  the chat as a user message. */
    updateBasketSilent: async (
      action: "qty" | "remove",
      itemId: string,
      quantity?: number,
    ) => {
      // Snapshot the item BEFORE the server update so we still know its
      // variant_id after a remove. Since 2026-06-10 basket adds mirror
      // straight into the live Shopify cart, so qty changes/removals here
      // must hit the real cart too — otherwise a customer who removes an
      // item in the canvas still has it in their actual cart (the inverse
      // of the joeplesher empty-cart bug).
      const target = canvas.basket?.items?.find((i) => i.id === itemId)
      try {
        const r = await fetch(`${opts.apiBaseUrl}/api/chat/basket`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionToken: opts.sessionToken,
            action,
            itemId,
            quantity,
          }),
        })
        if (!r.ok) return
        const data = (await r.json()) as { basket?: PendingBasket | null }
        if (data.basket) {
          setCanvas((c) => ({ ...c, basket: data.basket as PendingBasket }))
        }
        if (target?.variant_id) {
          const variantId = String(target.variant_id).replace(
            /^gid:\/\/shopify\/ProductVariant\//,
            "",
          )
          const newQty = action === "remove" ? 0 : Math.max(0, quantity ?? 1)
          fetch("/cart/change.js", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({ id: variantId, quantity: newQty }),
          })
            .then((cr) => {
              if (cr.ok) void notifyStorefrontCartChanged({ source: "basket-edit" })
              else console.warn("[dyc-pearl] cart/change.js failed", cr.status)
            })
            .catch((err) =>
              console.warn("[dyc-pearl] cart/change.js network error", err),
            )
        }
      } catch (err) {
        console.warn("[updateBasketSilent] failed:", err)
      }
    },
    /** Silent basket commit — bypasses a Pearl turn. The customer taps
     *  Checkout in the canvas BasketView, we POST commit directly, take
     *  the returned items, and POST them to /cart/add.js in the browser.
     *  No "Add the whole basket to my cart" message gets typed into the
     *  thread; no LLM round-trip. Caught 2026-06-03: Justin said the
     *  canvas Checkout "shouldnt need another validation check just get
     *  it in the cart". */
    commitBasketSilent: async (): Promise<void> => {
      try {
        const r = await fetch(`${opts.apiBaseUrl}/api/chat/basket`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sessionToken: opts.sessionToken,
            action: "commit",
          }),
        })
        if (!r.ok) return
        const data = (await r.json()) as {
          basket?: PendingBasket | null
          action?: { type?: string; items?: Array<{ id: string; quantity: number }> } | null
        }
        // Clear canvas basket UI optimistically — server already cleared
        // the server-side basket and we're about to write to Shopify.
        setCanvas((c) => ({
          ...c,
          basket: { items: [], subtotal: 0, updated_at: null },
        }))
        // Replay the basket_committed action through the same handler
        // the SSE stream uses, so cart writes + custom events fire once
        // and only once regardless of how commit was triggered.
        if (data.action && data.action.type === "basket_committed") {
          handleSideEffectAction(data.action as unknown as Record<string, unknown>)
        }
      } catch (err) {
        console.warn("[commitBasketSilent] failed:", err)
      }
    },
    /** Add a single variant STRAIGHT to the Shopify cart + open the theme
     *  cart drawer — no Pearl turn, no pending-basket "ready to checkout"
     *  step. Used by the build-summary "Add to cart" so the kit lands in the
     *  real cart the instant the customer taps it (Justin 2026-06-05: "the
     *  moment someone clicks add to cart it should add it to the shopify cart
     *  and open the cart"). Mirrors the `cart_added` SSE side-effect. */
    addToShopifyCart: async (
      variantId: string | number,
      quantity = 1,
      meta?: { productTitle?: string; source?: "card_tap" | "tool" | "kit_summary" },
    ): Promise<void> => {
      if (typeof window === "undefined") return
      const id = String(variantId).replace(
        /^gid:\/\/shopify\/ProductVariant\//,
        "",
      )
      if (!id) return
      try {
        const r = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ id, quantity }),
        })
        if (!r.ok) {
          const err = await r.json().catch(() => ({}))
          console.warn("[dyc-pearl] direct cart/add.js failed", r.status, err)
          window.dispatchEvent(
            new CustomEvent("dyc-pearl:cart-add-failed", {
              detail: { variantId: id, status: r.status, error: err },
            }),
          )
          return
        }
        // Success → fan out cart-changed events + open the theme cart drawer.
        await notifyStorefrontCartChanged({
          type: "cart_added",
          variantId: id,
          quantity,
        })
        // Beacon the add back to analytics so we can tell Pearl-SOLD orders (a
        // bought variant was added from a Pearl card) from Pearl-ASSISTED ones.
        // Fire-and-forget — never block or fail the cart add on this.
        void fetch(`${opts.apiBaseUrl}/api/chat/cart-add`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          keepalive: true,
          body: JSON.stringify({
            sessionToken: opts.sessionToken,
            conversationId: conversationIdRef.current ?? undefined,
            variantId: id,
            quantity,
            productTitle: meta?.productTitle,
            source: meta?.source ?? "card_tap",
          }),
        }).catch(() => {})
      } catch (err) {
        console.warn("[dyc-pearl] direct cart/add network error", err)
      }
    },
  }
}

export interface ConversationSummary {
  id: string
  preview: string
  updatedAt: string
}

/** An uploaded image to attach to an outgoing message (public bucket URL). */
export interface OutgoingAttachment {
  url: string
  contentType: string
}

// ── Action → attachment mapping ─────────────────────────────────────────

/** Translate a backend `action` SSE payload into 0+ attachments to render
 *  under the assistant bubble. Field names match `src/lib/chat/tools.ts`
 *  exactly — see the `action: {...}` literals there for ground truth. */
function mapActionToAttachments(
  data: Record<string, unknown>,
): MessageAttachment[] {
  const type = String(data.type ?? "")
  switch (type) {
    case "product_card": {
      const product = data.product as Record<string, unknown> | undefined
      if (!product) return []
      // Price comes either as a top-level `price` (staff /send-card) or as
      // the cheapest entry in variants[] (Pearl's show_product_card tool,
      // which always returns the variant matrix). Prefer the variant min
      // because for kits the customer should see the lowest tier — "from
      // $19.99" framing — rather than the picker's first row at random.
      const variants = (product.variants as Array<Record<string, unknown>>) ?? []
      const variantPrices = variants
        .map((v) => parseFloat(String(v.price ?? "")))
        .filter((n) => Number.isFinite(n))
      const minPrice = variantPrices.length ? Math.min(...variantPrices) : null
      const card: ProductCardData = {
        handle: String(product.handle ?? ""),
        name: String(product.title ?? ""),
        imageUrl: (product.image as string | null | undefined) ?? undefined,
        summary: stripHtml(String(product.description ?? "")) || undefined,
        productUrl: String(product.url ?? ""),
        price:
          (product.price as string | number | undefined)?.toString() ??
          (minPrice != null ? String(minPrice) : undefined),
        compareAtPrice:
          (product.compareAtPrice as string | number | undefined)?.toString() ??
          undefined,
        variants: (product.variants as ProductCardData["variants"]) ?? undefined,
        fitment: (product.fitment as ProductCardData["fitment"]) ?? undefined,
      }
      return [{ kind: "product", data: card }]
    }
    case "products": {
      // search_products returns multiple matches at once — render each
      // as a product card (capped at 4 to keep the bubble reasonable).
      // Field shape matches ChatProductInfo from search-products.ts:
      // { name, price, url, image, handle, description, ... }.
      const products = (data.products as Array<Record<string, unknown>>) ?? []
      return products.slice(0, 4).flatMap((p) => {
        const handle = String(p.handle ?? "")
        const url = String(p.url ?? "")
        if (!handle || !url) return []
        const card: ProductCardData = {
          handle,
          name: String(p.name ?? ""),
          imageUrl: (p.image as string | null | undefined) ?? undefined,
          summary: stripHtml(String(p.description ?? "")) || undefined,
          productUrl: url,
          price: (p.price as string | number | undefined)?.toString() ?? undefined,
          compareAtPrice:
            (p.compareAtPrice as string | number | undefined)?.toString() ??
            (p.compare_at_price as string | number | undefined)?.toString() ??
            undefined,
        }
        return [{ kind: "product" as const, data: card }]
      })
    }
    case "colors": {
      const colors = (data.colors as Array<Record<string, unknown>>) ?? []
      return colors.slice(0, 5).map((c) => {
        const card: ColorCardData = {
          name: String(c.color_name ?? ""),
          code: String(c.color_code ?? ""),
          brand: String(c.brand_name ?? ""),
          finish: c.finish as string | undefined,
          swatchUrl: String(c.image_url ?? ""),
          rgbHex: (c.rgb_hex as string | undefined) ?? undefined,
          productUrl: c.product_url as string | undefined,
          renders:
            (c.renders as { matte?: string; gloss?: string } | undefined) ??
            undefined,
        }
        return { kind: "color", data: card } satisfies MessageAttachment
      })
    }
    case "order_status": {
      const tl = (data.timeline as Array<Record<string, unknown>> | undefined) ?? []
      const le = data.lastEvent as Record<string, unknown> | null | undefined
      const card: TrackingCardData = {
        orderNumber: String(data.orderNumber ?? ""),
        status: String(data.status ?? ""),
        fulfillmentStatus: String(data.fulfillmentStatus ?? "UNFULFILLED"),
        carrier: (data.carrier as string | null | undefined) ?? undefined,
        trackingNumber: (data.trackingNumber as string | null | undefined) ?? undefined,
        trackingUrl: (data.trackingUrl as string | null | undefined) ?? undefined,
        shipDate: (data.shipDate as string | null | undefined) ?? undefined,
        eta: (data.eta as string | null | undefined) ?? undefined,
        deliveredAt: (data.deliveredAt as string | null | undefined) ?? undefined,
        shipToCity: (data.shipToCity as string | null | undefined) ?? undefined,
        lastEvent: le
          ? {
              occurredAt: String(le.occurredAt ?? ""),
              description: String(le.description ?? ""),
              location: String(le.location ?? ""),
            }
          : undefined,
        timeline: tl.map((e) => ({
          occurredAt: String(e.occurredAt ?? ""),
          description: String(e.description ?? ""),
          location: String(e.location ?? ""),
        })),
        items: (data.items as Array<{ name: string; quantity: number }> | undefined) ?? [],
        total: (data.total as string | null | undefined) ?? undefined,
      }
      return [{ kind: "tracking", data: card }]
    }
    case "discount": {
      return [
        {
          kind: "discount",
          data: {
            code: String(data.code ?? ""),
            label: String(data.message ?? data.code ?? ""),
            expiresAt: undefined,
          },
        },
      ]
    }
    case "youtube_videos": {
      // search_knowledge surfaces article-paired and topic-fallback
      // YouTube cards. Cap at 2 so the bubble doesn't tower.
      const videos = (data.videos as Array<Record<string, unknown>>) ?? []
      return videos.slice(0, 2).map((v) => {
        const card: VideoCardData = {
          videoId: String(v.videoId ?? ""),
          title: String(v.title ?? ""),
          thumbnail: String(v.thumbnail ?? ""),
          url: String(v.url ?? ""),
        }
        return { kind: "video", data: card } satisfies MessageAttachment
      })
    }
    case "build_car_kit_step": {
      return [{
        kind: "build_step" as const,
        data: {
          productHandle: String(data.productHandle ?? ""),
          partialSummary: data.partialSummary as never,
          nextStep: data.nextStep as never,
        },
      }]
    }
    case "build_car_kit_summary": {
      return [{
        kind: "build_summary" as const,
        data: {
          productHandle: String(data.productHandle ?? ""),
          variantId: Number(data.variantId ?? 0),
          variantTitle: String(data.variantTitle ?? ""),
          price: Number(data.price ?? 0),
          compareAtPrice: data.compareAtPrice != null ? Number(data.compareAtPrice) : undefined,
          kitContents: (data.kitContents as Array<{ name: string; quantity: number; imageUrl?: string }>) ?? [],
          // Phase 1 enhancement: merchant-curated upsells the customer can
          // toggle on the summary card before confirming add-to-cart.
          upsellProducts: (data.upsellProducts as Array<{
            id: string
            handle: string
            name: string
            price: number
            imageUrl?: string
          }>) ?? [],
          activeTags: (data.activeTags as string[]) ?? [],
          addToCartUrl: String(data.addToCartUrl ?? ""),
          colorRenders: data.colorRenders as { matte?: string; gloss?: string } | undefined,
        },
      }]
    }
    default:
      return []
  }
}

/** Side-effect actions (navigate, cart_added, handoff) execute storefront
 *  effects in addition to any attachment rendering done by
 *  `mapActionToAttachments`. Action payloads are flat — see the
 *  `action: {...}` literals in `src/lib/chat/tools.ts` for ground truth. */
/**
 * Translate an agent-side `chat_messages.display_data` payload into the
 * widget's Message shape (text + attachments). The dashboard's staff Copilot
 * writes a few specific shapes:
 *
 *   - `{ type: "product_card", product: {...} }` — matches Pearl's mapper exactly
 *   - `{ type: "youtube_video", videoId, title, thumbnail, url }` — singular
 *      (Pearl emits `youtube_videos: [...]` plural); wrap and forward
 *   - `{ type: "image", url, alt }` — render as a markdown image in the bubble
 *      text since the widget has no inline-image attachment kind yet
 *
 * Returns the parts the polling loop merges into the final Message. Falls
 * through to {} for unknown shapes so we don't drop the bubble entirely.
 */
function mapAgentDisplayData(
  dd: Record<string, unknown> | undefined,
): { text?: string; attachments?: MessageAttachment[] } {
  if (!dd) return {}
  const type = String(dd.type ?? "")
  switch (type) {
    case "product_card":
      // Same shape as Pearl's product_card → reuse mapper directly.
      return { attachments: mapActionToAttachments(dd) }
    case "youtube_video": {
      // Wrap into the plural shape Pearl uses and reuse the same mapper.
      const wrapped = { type: "youtube_videos", videos: [dd] }
      return { attachments: mapActionToAttachments(wrapped) }
    }
    case "color_card": {
      const c = dd as Record<string, unknown>
      const color = (c.color ?? {}) as Record<string, unknown>
      const data: ColorCardData = {
        name: String(color.name ?? ""),
        code: String(color.code ?? ""),
        brand: String(color.brand ?? ""),
        finish: color.finish ? String(color.finish) : undefined,
        swatchUrl: String(c.image ?? ""),
        rgbHex: color.rgbHex ? String(color.rgbHex) : undefined,
        productUrl: c.productUrl ? String(c.productUrl) : undefined,
        productHandle: c.productHandle ? String(c.productHandle) : undefined,
        variants: (c.variants as ColorCardData["variants"]) ?? undefined,
        fitment: (c.fitment as ColorCardData["fitment"]) ?? undefined,
      }
      return { attachments: [{ kind: "color", data }] }
    }
    case "image": {
      // Map to a real image attachment so the bubble renders an <img>
      // instead of synthesized markdown (which cleanForDisplay would strip).
      const url = String(dd.url ?? "")
      if (!url) return {}
      const alt = typeof dd.alt === "string" && dd.alt ? dd.alt : undefined
      return { attachments: [{ kind: "image" as const, data: { url, alt } }] }
    }
    case "messaging_handoff": {
      // Branded "continue on iMessage / WhatsApp" card (#7). Each URL is
      // optional — the renderer shows only the buttons whose url is present.
      const whatsappUrl = dd.whatsappUrl ? String(dd.whatsappUrl) : undefined
      const imessageUrl = dd.imessageUrl ? String(dd.imessageUrl) : undefined
      if (!whatsappUrl && !imessageUrl) return {}
      return {
        attachments: [
          { kind: "messaging_handoff" as const, data: { whatsappUrl, imessageUrl } },
        ],
      }
    }
    default:
      return {}
  }
}

/** Tell the storefront theme that the cart just changed. Fans out across
 *  every event name common DipYourCar / Shopify themes listen for, so
 *  whichever the active theme hooks up will fire. Also re-fetches /cart.js
 *  so any theme that polls cart state on event gets fresh data. Without
 *  this fan-out, the cart drawer + header badge only refresh on the next
 *  full page reload — which Justin called out as "no good" on 2026-06-03.
 *
 *  The list mirrors what Shopify Online Store 2.0 + DipYourCar's custom
 *  theme listen for. Adding more is harmless (unknown events are silent);
 *  removing any of these risks regressing a theme variant. */
async function notifyStorefrontCartChanged(detail: unknown): Promise<void> {
  if (typeof window === "undefined") return
  // Re-fetch /cart.js so any theme that does `fetch('/cart.js')` on
  // event-fire gets the current state without us computing it client-side.
  let cart: unknown = null
  try {
    const r = await fetch("/cart.js", { credentials: "include" })
    if (r.ok) cart = await r.json()
  } catch {
    /* network blip — events still fire; theme can self-refresh */
  }
  const payload = { detail: { ...(detail as object), cart } }
  // dyc-pearl namespace — for the DYC theme.
  window.dispatchEvent(new CustomEvent("dyc-pearl:cart-added", payload))
  window.dispatchEvent(new CustomEvent("dyc-pearl:cart-updated", payload))
  // Generic Shopify-theme events — picked up by Dawn-derived themes.
  window.dispatchEvent(new CustomEvent("cart:refresh", payload))
  window.dispatchEvent(new CustomEvent("cart:updated", payload))
  // Shopify document-level "publish" pattern (Dawn cart-notification).
  document.dispatchEvent(new CustomEvent("cart:refresh", payload))
  document.dispatchEvent(new CustomEvent("cart:updated", payload))

  // ── Live DYC theme integration (verified via Playwright 2026-06-05) ──
  // The custom DYC events above are NOT what this theme listens for, which
  // is why "add worked but the cart never opened/updated". The live theme:
  //   • updates the cart-count badge via Shopify's pub/sub — its
  //     <cart-drawer> does `subscribe(PUB_SUB_EVENTS.cartUpdate, …)`.
  //   • refreshes the drawer contents via <cart-drawer>._refreshSections().
  //   • opens the drawer via <cart-drawer>.show()  (NOT window.CartDrawer.open).
  // We drive all three (best-effort) + keep the legacy CartDrawer.open()
  // for any other theme. Every call is guarded so a theme that lacks one
  // path still benefits from the others.
  const w = window as unknown as {
    publish?: (evt: unknown, data: unknown) => void
    PUB_SUB_EVENTS?: { cartUpdate?: unknown }
    CartDrawer?: { open?: () => void }
  }
  try {
    if (typeof w.publish === "function" && w.PUB_SUB_EVENTS?.cartUpdate) {
      // Standard Shopify section-rendering pub/sub → updates the count badge.
      w.publish(w.PUB_SUB_EVENTS.cartUpdate, { source: "dyc-pearl", cartData: cart })
    }
  } catch {
    /* pub/sub not present on this theme — events + drawer calls still fire */
  }
  try {
    const cd = document.querySelector("cart-drawer") as
      | (Element & { _refreshSections?: () => void; show?: () => void })
      | null
    if (cd) {
      cd._refreshSections?.() // re-fetch + swap the drawer's cart HTML
      cd.show?.() // open the drawer (DYC theme)
    }
    if (typeof w.CartDrawer?.open === "function") w.CartDrawer.open() // legacy/other themes
  } catch {
    /* no drawer API — themes that lack one will refresh from the events */
  }
}

function handleSideEffectAction(data: Record<string, unknown>): void {
  const type = String(data.type ?? "")
  if (typeof window === "undefined") return

  switch (type) {
    case "basket_update": {
      // ── Basket adds mirror STRAIGHT into the real Shopify cart ──
      // The basket was designed as a staging pen ("review in canvas, then
      // commit_basket") but Pearl's copy says "cart", so customers believe
      // the item is in their real cart and never commit — they reach
      // checkout empty-handed. Caught 2026-06-10 (joeplesher@gmail.com:
      // two kit builds "Added" → real cart never touched). Now every item
      // added to the basket is POSTed to /cart/add.js immediately; the
      // canvas basket is a mirror of the real cart, not a separate one.
      // (commit_basket server-side now emits empty items so it can't
      // double-add; removals in canvas still only affect the mirror.)
      const change = data.change as
        | { kind?: string; addedIds?: string[] }
        | undefined
      if (change?.kind !== "added" || !Array.isArray(change.addedIds)) break
      const basket = data.basket as
        | { items?: Array<{ id?: string; variant_id?: string; quantity?: number }> }
        | undefined
      const addedIds = new Set(change.addedIds)
      const items = (basket?.items ?? [])
        .filter((i) => i.id && addedIds.has(i.id))
        .map((i) => ({
          id: String(i.variant_id ?? "").replace(
            /^gid:\/\/shopify\/ProductVariant\//,
            "",
          ),
          quantity:
            Number.isFinite(Number(i.quantity)) && Number(i.quantity) > 0
              ? Number(i.quantity)
              : 1,
        }))
        .filter((i) => i.id)
      if (items.length === 0) break
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items }),
      })
        .then((r) => {
          if (!r.ok) {
            return r.json().then((err) => {
              console.warn("[dyc-pearl] basket→cart add failed", r.status, err)
              window.dispatchEvent(
                new CustomEvent("dyc-pearl:cart-add-failed", {
                  detail: { items, status: r.status, error: err },
                }),
              )
            })
          }
          void notifyStorefrontCartChanged(data)
        })
        .catch((err) => {
          console.warn("[dyc-pearl] basket→cart network error", err)
        })
      break
    }
    case "navigate": {
      const url = String(data.url ?? "")
      if (!url) break
      // Brief delay so Pearl's confirmation message renders before the
      // page changes — feels less abrupt to the customer.
      setTimeout(() => {
        window.location.href = url
      }, 700)
      break
    }
    case "cart_added": {
      // Pearl's add_to_cart tool returns metadata only — it has no way to
      // touch the customer's cart from the server (cart state lives in the
      // browser cookies). The actual add has to happen here, in the widget,
      // against the storefront's /cart/add.js endpoint with the variant id
      // Pearl chose. Without this, Pearl's "added to your cart!" reply is
      // theatrical and the customer's cart never changes.
      //
      // The fetch monkeypatch in usePageContext.ts will catch /cart/add.js
      // as a cart-write and trigger a /cart.js re-fetch + cart_update sync
      // to chat_sessions, which feeds the dashboard copilot's "Now" card.
      // So a successful add cascades all the way to staff visibility.
      // Strip Admin GraphQL GID prefix defensively — show_product_card now
      // does this server-side, but anything else that might emit a cart_added
      // action (future tools, manual events) shouldn't need to know about
      // Shopify's storefront/admin id duality.
      const rawVariantId = data.variantId
      const variantId =
        typeof rawVariantId === "string"
          ? rawVariantId.replace(/^gid:\/\/shopify\/ProductVariant\//, "")
          : rawVariantId
      const quantity = typeof data.quantity === "number" ? data.quantity : 1
      // Store-default bundles (2026-07-13): add_to_cart resolves the OEM
      // undercoat server-side and sends it as bundledItems — POST the whole
      // set in ONE /cart/add.js call so the basecoat and its undercoat land
      // together, matching the product page's default behavior.
      const bundledItems = Array.isArray(data.bundledItems)
        ? (data.bundledItems as Array<Record<string, unknown>>)
            .map((b) => ({
              id: String(b.variantId ?? "").replace(
                /^gid:\/\/shopify\/ProductVariant\//,
                "",
              ),
              quantity:
                Number.isFinite(Number(b.quantity)) && Number(b.quantity) > 0
                  ? Number(b.quantity)
                  : 1,
            }))
            .filter((b) => b.id)
        : []
      if (variantId) {
        fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            items: [
              { id: String(variantId), quantity },
              ...bundledItems,
            ],
          }),
        })
          .then((r) => {
            if (!r.ok) {
              // Most common failure: Shopify returns 422 with
              // { status: 422, message: 'Cart Error', description: '...' }
              // when the variant ID doesn't exist or is sold out. Surface
              // the error to the storefront so any theme listener can react.
              return r.json().then((err) => {
                console.warn("[dyc-pearl] cart/add.js failed", r.status, err)
                window.dispatchEvent(
                  new CustomEvent("dyc-pearl:cart-add-failed", {
                    detail: { variantId, status: r.status, error: err },
                  }),
                )
              })
            }
            // Success. Real-time cart-update broadcast — fan out to every
            // event name common Shopify themes listen for, so the cart
            // drawer/badge refreshes without a page refresh. Themes that
            // ignore one will catch another. Caught 2026-06-03: "add to
            // cart works but on a page refresh which is no good".
            void notifyStorefrontCartChanged(data)
          })
          .catch((err) => {
            console.warn("[dyc-pearl] cart/add.js network error", err)
          })
      } else {
        console.warn(
          "[dyc-pearl] cart_added action missing variantId — Pearl didn't load the product first",
          data,
        )
      }
      break
    }
    case "handoff": {
      // Customer-facing signal is already in Pearl's reply text; surface
      // a custom event so any storefront listeners can react too.
      window.dispatchEvent(
        new CustomEvent("dyc-pearl:handoff", { detail: data }),
      )
      break
    }
    case "basket_committed": {
      // The customer just confirmed their pending basket. Shopify cart
      // state lives in BROWSER cookies, so the actual cart write has to
      // happen here — the server already cleared the pending basket but
      // it never POSTed to Shopify (see basket.ts commitBasket comment).
      // The action carries a pre-shaped { items: [{id, quantity}, ...] }
      // array ready for /cart/add.js. Multi-line POST so all items add
      // atomically in a single request. Strips GID prefix defensively.
      const rawItems = Array.isArray(data.items) ? data.items : []
      const items = rawItems
        .map((it) => {
          const raw = (it as Record<string, unknown>).id
          const id =
            typeof raw === "string"
              ? raw.replace(/^gid:\/\/shopify\/ProductVariant\//, "")
              : String(raw ?? "")
          const qty = Number((it as Record<string, unknown>).quantity ?? 1)
          return { id, quantity: Number.isFinite(qty) && qty > 0 ? qty : 1 }
        })
        .filter((it) => it.id)
      if (items.length === 0) {
        // Empty-basket commit (or malformed action). Skip the POST.
        window.dispatchEvent(
          new CustomEvent("dyc-pearl:basket-committed", { detail: data }),
        )
        break
      }
      fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ items }),
      })
        .then((r) => {
          if (!r.ok) {
            // Shopify returns 422 with { description: "..." } when any
            // line item is invalid (variant gone, sold out, qty cap).
            // The entire request fails — nothing is added. Surface to
            // storefront listeners so the theme can toast an error.
            return r.json().then((err) => {
              console.warn(
                "[dyc-pearl] basket commit cart/add.js failed",
                r.status,
                err,
              )
              window.dispatchEvent(
                new CustomEvent("dyc-pearl:basket-commit-failed", {
                  detail: { items, status: r.status, error: err },
                }),
              )
            })
          }
          // Real-time cart-update broadcast — see notifyStorefrontCartChanged
          // for the full event fan-out; the widget-side cart-added flow
          // calls the same helper so a single source of truth for "how do
          // we tell the theme the cart changed".
          void notifyStorefrontCartChanged({ ...data, source: "basket_committed" })
        })
        .catch((err) => {
          console.warn("[dyc-pearl] basket commit cart/add.js network error", err)
        })
      break
    }
    // product_card / colors / order_status / discount fall through —
    // those are handled as attachments via mapActionToAttachments.
  }
}

function stripHtml(html: string): string {
  if (!html) return ""
  // Cheap server-safe strip; we don't have a DOM in workers/node.
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 240)
}
